'use client';

import { IconContext } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import React, { createContext, useState } from 'react';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import { cssVar } from '@zoomit/primitives';
import { theme } from '@zoomit/zoomit-ds';

import type { GroupingDesktopTemplates, GroupingMobileTemplates } from '../types/groupings';

export type ExternalOrigin = {
  baseUrl: string;
  baseMediaUrl: string;
  baseApiUrl: string;
};

export type WebKitCustomizationType = {
  signInReasons: { description: string; icon: React.JSX.Element }[];
  defaultDesktopCategoryTemplate?: GroupingDesktopTemplates;
  defaultMobileCategoryTemplate?: GroupingMobileTemplates;
  tenantPersianTitle: string;
  tenantTitle: string;
  basePath: string;
  catalogProvider: {
    baseUrl: string;
    baseMediaUrl: string;
    baseApiUrl: string;
    originUrl: string;
    name: string;
  };
  search?: {
    placeholder?: string;
  };
  tenantLogoRenderer: (props: { fill: string }) => ReactNode;
  externalOrigin: {
    [key: string]: ExternalOrigin;
  };
  emptyStatesPublicPath: {
    addComment: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noComment: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    emptyItem?: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noArticle?: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noProduct?: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noProductCompare?: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    sthWentWrong: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noResult?: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
    noNotification: {
      publicSrc: string;
      width?: number;
      height?: number;
    };
  };
};

export const WebKitCustomizationContext = createContext<StoreApi<WebKitCustomizationType> | null>(null);

type Props = {
  children: ReactNode;
  initialData: WebKitCustomizationType;
};

export const WebKitCustomizationProvider = (props: Props) => {
  const { children, initialData } = props;

  const [store] = useState(() =>
    createStore<WebKitCustomizationType>(() => ({
      signInReasons: initialData.signInReasons,
      defaultDesktopCategoryTemplate: initialData.defaultDesktopCategoryTemplate,
      defaultMobileCategoryTemplate: initialData.defaultMobileCategoryTemplate,
      tenantTitle: initialData.tenantTitle,
      tenantPersianTitle: initialData.tenantPersianTitle,
      basePath: initialData.basePath,
      emptyStatesPublicPath: initialData.emptyStatesPublicPath,
      catalogProvider: {
        baseMediaUrl: initialData.catalogProvider.baseMediaUrl,
        baseApiUrl: initialData.catalogProvider.baseApiUrl,
        baseUrl: initialData.catalogProvider.baseUrl,
        originUrl: initialData.catalogProvider.originUrl,
        name: initialData.catalogProvider.name,
      },
      externalOrigin: initialData.externalOrigin,
      tenantLogoRenderer: initialData.tenantLogoRenderer,
      search: initialData.search,
    })),
  );

  return (
    <WebKitCustomizationContext.Provider value={store}>
      <IconContext.Provider
        value={{
          color: cssVar(theme.grey1),
          size: 16,
          weight: 'bold',
        }}
      >
        {children}
      </IconContext.Provider>
    </WebKitCustomizationContext.Provider>
  );
};
